import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, Button, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { FileUploadArea, DescriptionInput, ProcessingView, ErrorView, CompleteView } from './components';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../common/cards/alert-card/AlertCard';
import { UploadedFile, SetupAnalyseRequest, ProgramsAnalyseResponse } from './stages/types';
import { ProgramsResultsView, getInitialProgramSelections } from './stages/programs';
import { useSetupDialogStyles } from './setupDialogStyles';

type Step = 'upload' | 'processing' | 'results' | 'applying' | 'complete' | 'error';

interface ProgramsSetupDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (data: Record<string, unknown>) => void;
  questionnaireAnswers?: Record<string, unknown>;
}

interface SaveProgramRequest {
  programId?: string;
  name: string;
  description?: string;
  active: boolean;
  isHidden: boolean;
  isRestrictedProgram: boolean;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export function ProgramsSetupDialog({ open, onClose, onComplete, questionnaireAnswers }: ProgramsSetupDialogProps) {
  const classes = useSetupDialogStyles();
  const { lightTextColor } = getAlertCardColors('info');

  const [step, setStep] = useState<Step>('upload');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [description, setDescription] = useState('');
  const [results, setResults] = useState<ProgramsAnalyseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPrograms, setSelectedPrograms] = useState<Set<string>>(new Set());
  const [editedPrograms, setEditedPrograms] = useState<Map<string, { name: string; description: string }>>(new Map());
  const [programsCreated, setProgramsCreated] = useState(0);

  const analyseFetch = useEndpointFetch<SetupAnalyseRequest, ProgramsAnalyseResponse>('/vm/setup/analyse');
  const saveProgramFetch = useEndpointFetch<SaveProgramRequest, { programId: string }>('/vm/program/save');

  const handleFilesAdded = useCallback((newFiles: UploadedFile[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleFileRemoved = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_unused, i) => i !== index));
  }, []);

  const handleAnalyse = async () => {
    setStep('processing');
    setError(null);

    try {
      const fileData = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          content: await fileToBase64(f.file),
        }))
      );

      const response = await analyseFetch.run({
        section: 'programs',
        files: fileData,
        description: description || undefined,
        questionnaireAnswers,
      });

      if (response.ok && response.data?.data) {
        const { data } = response.data;
        setResults(data);
        const selections = getInitialProgramSelections(data);
        setSelectedPrograms(selections.programs);
        setStep('results');
      } else {
        const errorMessage = response.data?.message || 'Failed to analyse documents';
        setError(errorMessage);
        setStep('error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFiles([]);
    setDescription('');
    setResults(null);
    setError(null);
    setSelectedPrograms(new Set());
    setEditedPrograms(new Map());
    setProgramsCreated(0);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleApply = async () => {
    if (!results) return;

    setStep('applying');

    try {
      const programsToCreate = results.programs
        .filter((program) => selectedPrograms.has(program.name))
        .map((program) => {
          const edited = editedPrograms.get(program.name);
          return {
            name: edited?.name ?? program.name,
            description: edited?.description ?? program.description ?? undefined,
            active: true,
            isHidden: false,
            isRestrictedProgram: false,
          };
        });

      const responses = await programsToCreate.reduce<Promise<Array<{ ok: boolean }>>>(async (acc, programData) => {
        const prev = await acc;
        try {
          const response = await saveProgramFetch.run(programData);
          return [...prev, response || { ok: false }];
        } catch (e) {
          return [...prev, { ok: false }];
        }
      }, Promise.resolve([]));

      const created = responses.filter((r) => r.ok).length;
      setProgramsCreated(created);
      if (onComplete && results) {
        const programsCreatedList = results.programs
          .filter((p) => selectedPrograms.has(p.name))
          .map((p) => {
            const edited = editedPrograms.get(p.name);
            return {
              name: edited?.name ?? p.name,
              description: edited?.description ?? p.description,
            };
          });
        onComplete({
          analysis: results.analysis,
          programsCreated: programsCreatedList,
          programsCreatedCount: created,
        });
      }
      setStep('complete');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleComplete = () => {
    handleClose();
  };

  const toggleProgram = (programName: string) => {
    setSelectedPrograms((prev) => {
      const next = new Set(prev);
      if (next.has(programName)) next.delete(programName);
      else next.add(programName);
      return next;
    });
  };

  const handleEditProgram = (originalName: string, newName: string, newDescription: string) => {
    setEditedPrograms((prev) => {
      const next = new Map(prev);
      next.set(originalName, { name: newName, description: newDescription });
      return next;
    });
  };

  const getSubtitle = () => {
    switch (step) {
      case 'upload':
        return 'Describe your programs and operations';
      case 'processing':
        return 'Analysing your documents...';
      case 'results':
        return 'Review and select programs to create';
      case 'applying':
        return 'Creating programs...';
      case 'complete':
        return 'Programs created successfully';
      case 'error':
        return 'Something went wrong';
      default:
        return '';
    }
  };

  const canAnalyse = files.length > 0 || description.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={handleClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography className={classes.title}>Programs Setup</Typography>
        <Typography className={classes.subtitle}>{getSubtitle()}</Typography>
      </Box>

      <DialogContent className={classes.content}>
        {step === 'upload' && (
          <>
            <Box mb={2}>
              <AlertCard variant='info' title='What is a Program?'>
                <AlertCardBody>
                  <Typography style={{ fontSize: 14, color: lightTextColor }}>
                    A Program is a way to group or segment volunteers. This could be location-based (e.g. Sydney,
                    Melbourne), event-based, or based on the type of activities your organisation runs.
                  </Typography>
                </AlertCardBody>
              </AlertCard>
            </Box>
            <Typography className={classes.description}>
              Upload documents that describe your organisation structure (org charts, program descriptions, location
              lists) or describe how you want to organise your volunteering programs.
            </Typography>
            <DescriptionInput
              value={description}
              onChange={setDescription}
              label='Describe your programs'
              placeholder='E.g., "We have about 50 volunteers across Sydney, Melbourne, and Brisbane running Youth Services and Community Support programs"'
              helperText='Include your approximate volunteer count to help us suggest the right structure'
            />
            <Typography className={classes.sectionTitle}>Upload documents</Typography>
            <Box className={classes.uploadSection}>
              <FileUploadArea files={files} onFilesAdded={handleFilesAdded} onFileRemoved={handleFileRemoved} />
            </Box>
          </>
        )}

        {step === 'processing' && (
          <ProcessingView title='Analysing your documents...' subtitle='Identifying program structure' />
        )}

        {step === 'results' && results && (
          <ProgramsResultsView
            results={results}
            selectedPrograms={selectedPrograms}
            editedPrograms={editedPrograms}
            onToggleProgram={toggleProgram}
            onEditProgram={handleEditProgram}
          />
        )}

        {step === 'applying' && <ProcessingView title='Creating programs...' subtitle='Please wait' />}

        {step === 'complete' && (
          <CompleteView
            title='Programs Created'
            subtitle={`${programsCreated} program${programsCreated !== 1 ? 's' : ''} created successfully`}
            linkText='Manage Programs'
            linkHref='/admin/program-management'
          />
        )}

        {step === 'error' && error && <ErrorView message={error} onRetry={handleReset} />}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {step === 'upload' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Cancel
            </Button>
            <Button className={classes.primaryButton} onClick={handleAnalyse} disabled={!canAnalyse}>
              Analyse
            </Button>
          </>
        )}

        {step === 'results' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleReset}>
              Start Over
            </Button>
            <Button className={classes.primaryButton} onClick={handleApply} disabled={selectedPrograms.size === 0}>
              Create {selectedPrograms.size} Program{selectedPrograms.size !== 1 ? 's' : ''}
            </Button>
          </>
        )}

        {step === 'complete' && (
          <>
            <Box />
            <Button className={classes.primaryButton} onClick={handleComplete}>
              Done
            </Button>
          </>
        )}

        {step === 'error' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Close
            </Button>
            <Button className={classes.primaryButton} onClick={handleReset}>
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
